// src/utils/txLabel.js
// P4-8 Group 3 (31 Oct 2026) - how a money row and a rupee figure read on screen.
//
// WHY THIS FILE EXISTS
// Home Recent Transactions labelled every row that was not income as "Expense",
// and a vendor bill row (pbill_) has no description, so it read "Txn". Payments,
// reversals and opening balances are not expenses either. Home's figures also
// printed raw float sums (₹5,58,736.997), and the bill view printed a negative
// round off as ₹-0.50. Display only: nothing here decides what counts as income,
// expense or money movement. That stays in khaata.js / cashBook.js.
// Pure (no Firebase) -> covered by src/utils/txLabel.test.js (Jest).

import { roundHalfUp } from "./gstBill";

const clean = (v) => String(v ?? "").trim();

/**
 * Rounded to paise first (half-up, no float drift), then en-IN: a whole rupee shows
 * no decimals, anything else exactly 2. No figure, or one that is not a number, reads ₹0.
 * @returns {string} "₹5,58,737" / "₹1,06,199.50" / "−₹1,234.50"
 */
export const formatRupees = (n) => {
  const value = roundHalfUp(n, 2);
  if (!Number.isFinite(value) || value === 0) return "₹0";
  const rupees = Math.abs(value);
  const decimals = Number.isInteger(rupees) ? 0 : 2;
  const text = rupees.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${value < 0 ? "−" : ""}₹${text}`;
};

/** @returns {string} "−₹0.50" / "+₹0.50" / "₹0.00", or "-" with no figure */
export const signedRupees = (n) => {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "-";
  const value = Number(n);
  const text = Math.abs(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (text === "0.00") return "₹0.00";
  return `${value < 0 ? "−" : "+"}₹${text}`;
};

const NOT_EXPENSE_TYPES = {
  payment:          "Payment",
  payment_reversal: "Reversal",
  opening_balance:  "Opening balance",
};

// tone: income = green, expense = red, bill = amber, neutral = grey
/**
 * @param {Object} tx a financial_transactions row
 * @returns {{ description: string, typeLabel: string, tone: string }}
 */
export const txRowLabel = (tx = {}) => {
  if (tx.reference_type === "purchase_bill") {
    const invoiceNo = clean(tx.vendor_invoice_no);
    const vendor = clean(tx.vendor_name);
    const bill = invoiceNo ? `Bill ${invoiceNo}` : "Proforma bill";
    return { description: vendor ? `${bill} · ${vendor}` : bill, typeLabel: "Bill", tone: "bill" };
  }

  const own = tx.description || tx.project;

  const label = NOT_EXPENSE_TYPES[tx.type];
  if (label) return { description: own || clean(tx.party_name) || label, typeLabel: label, tone: "neutral" };

  return {
    description: own || "Txn",
    typeLabel:   tx.type === "income" ? "Income" : "Expense",
    tone:        tx.type === "income" ? "income" : "expense",
  };
};
